// #2355 follow-up: ethers は **型だけ**静的に取る (型は build で消えるので bundle に
// 入らない)。実体は使うときに読み込む — 理由は lib/ethers.ts の冒頭を参照
import type { Contract, TransactionReceipt } from "ethers";
import { NETWORK, getExplorerTxLink } from "./network";
import { getEthersProvider, getEthersSigner, loadEthers } from "./ethers";

// Minimal ERC-20 ABI (only what we need)
const ERC20_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
];

// Transfer confirmation timeout (60 seconds)
const TX_TIMEOUT_MS = 60_000;

/**
 * Get a read-only JPYC contract instance.
 */
async function getReadOnlyContract(): Promise<Contract | null> {
  const provider = await getEthersProvider();
  if (!provider) return null;
  const { Contract } = await loadEthers();
  return new Contract(NETWORK.jpycAddress, ERC20_ABI, provider);
}

/**
 * Get a JPYC contract instance connected to the signer (for writes).
 */
async function getSignerContract(): Promise<Contract> {
  const signer = await getEthersSigner();
  const { Contract } = await loadEthers();
  return new Contract(NETWORK.jpycAddress, ERC20_ABI, signer);
}

/**
 * Query on-chain JPYC balance for a wallet address.
 * Returns balance as a whole number (integer JPY equivalent).
 * Returns null if query fails.
 */
export async function getJpycBalance(
  walletAddress: string
): Promise<number | null> {
  try {
    const contract = await getReadOnlyContract();
    if (!contract) return null;

    const { formatUnits } = await loadEthers();
    const rawBalance: bigint = await contract.balanceOf(walletAddress);
    const balance = Number(formatUnits(rawBalance, NETWORK.jpycDecimals));
    return Math.floor(balance);
  } catch (err) {
    console.warn("[JPYC] Balance query failed:", err);
    return null;
  }
}

/**
 * Transfer JPYC to a store wallet address on-chain.
 * Waits for 1 confirmation with 60s timeout.
 * Returns { txHash, explorerLink } on success.
 */
export async function transferJpyc(
  toAddress: string,
  amountJpy: number
): Promise<{ txHash: string; explorerLink: string }> {
  const contract = await getSignerContract();
  const { parseUnits } = await loadEthers();
  const amountWei = parseUnits(String(amountJpy), NETWORK.jpycDecimals);

  // Send the transaction
  const tx = await contract.transfer(toAddress, amountWei);
  console.log("[JPYC] Transfer tx sent:", tx.hash);

  const explorerLink = getExplorerTxLink(tx.hash);

  // Wait for confirmation with timeout
  const confirmPromise: Promise<TransactionReceipt | null> = tx.wait(1);
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(
      () => reject(new Error("Transaction confirmation timeout")),
      TX_TIMEOUT_MS
    )
  );

  try {
    const receipt = await Promise.race([confirmPromise, timeoutPromise]);
    if (receipt && receipt.status === 0) {
      throw new Error("Transaction reverted");
    }
    console.log("[JPYC] Transfer confirmed:", tx.hash);
  } catch (err) {
    // If timeout, attach txHash so the caller can still show it
    if (
      err instanceof Error &&
      err.message === "Transaction confirmation timeout"
    ) {
      console.warn(
        "[JPYC] Confirmation timeout, tx may still confirm:",
        tx.hash
      );
      const timeoutError = Object.assign(
        new Error("Transaction confirmation timeout"),
        { txHash: tx.hash, explorerLink }
      );
      throw timeoutError;
    }
    throw err;
  }

  return { txHash: tx.hash, explorerLink };
}
